import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useDrawnBox } from "./drag-gesture";
import { ZOOM_FIT, useHoldsTheCentre, wholeLevel } from "./figure-zoom";
import { usePan } from "./pan-gesture";

/**
 * The size the drawing is drawn at, or nothing while either half of the sum is unknown.
 *
 * The drawing takes the width it is given and runs as far down as its own shape says,
 * whichever way round it is. Fitting a tall one to the height instead would leave it
 * narrow between two empty margins, and the drawing is the only part of the frame a
 * numeral can be placed on, so the margins would be room the attorney cannot use. What
 * runs past the bottom is reached by scrolling the window it sits in.
 *
 * Zoom multiplies that fitted width rather than transforming the box, which is what keeps
 * every pin, handle and hit target the same number of pixels across at any level: the marks
 * are fractions of this box, so growing it moves them and leaves them the size they were.
 * A transform would scale the marks with the artwork and blur both.
 */
function drawnAt(
  room: { width: number; height: number } | undefined,
  drawing: number | undefined,
  zoom: number,
): { width: number; height: number } | undefined {
  if (room === undefined || drawing === undefined || room.width === 0)
    return undefined;
  return {
    width: room.width * zoom,
    height: (room.width / drawing) * zoom,
  };
}

/** How far past its window a drawing must run before dragging it means anything. */
const PAN_SLACK = 1;

/** Whether the drawing is bigger than the window it sits in, by more than a rounding. */
function overflowing(
  room: { width: number; height: number } | undefined,
  drawn: { width: number; height: number } | undefined,
): boolean {
  if (room === undefined || drawn === undefined) return false;
  return (
    drawn.width > room.width + PAN_SLACK ||
    drawn.height > room.height + PAN_SLACK
  );
}

/**
 * One drawing, framed the way both branches of the Define surface frame it.
 *
 * Whatever is laid over the drawing renders inside a positioned box that is exactly the
 * image, inside the frame rather than around it. Points are fractions of the drawing
 * itself, and the rasterizer applies them to the image alone, so an overlay that measured
 * a box with the frame's border in it would store every point off by a border width. That
 * is why the box the drawing is fitted into is chosen here rather than left to
 * `object-fit`, which paints inside a box without changing it: the picture would move and
 * the overlay would not.
 *
 * The frame is also the window a zoomed drawing scrolls inside. It keeps the fitted size
 * and the border while the drawing outgrows it, so the panel chrome, the toolbar and the
 * registry stay where the attorney last saw them however far into a drawing they are.
 */
export function FigureDrawing({
  thumbnailUrl,
  filename,
  testId = "define-drawing",
  onError,
  fills = false,
  zoom = ZOOM_FIT,
  onWhole,
  children,
}: {
  thumbnailUrl: string;
  filename: string;
  /** Which surface is showing the drawing, for tests that select on one. */
  testId?: string;
  /** Told when the drawing does not arrive, for a surface that says so. */
  onError?: () => void;
  /**
   * Whether the frame takes the room it is given and sits the whole drawing inside it.
   *
   * Off, the drawing takes the width it is given and runs as far down the page as it
   * needs to, which is what a column on a scrolling page wants. On, it is the room that is
   * fixed and the drawing that fits, so nothing runs off the bottom of a screen the
   * attorney cannot scroll.
   */
  fills?: boolean;
  /** How many times the fitted size the drawing is drawn at, which only a filled frame has. */
  zoom?: number;
  /** Told the level at which the whole drawing is in view, once the frame and the image are measured. */
  onWhole?: (whole: number) => void;
  children?: ReactNode;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const room = useDrawnBox(frameRef);
  const [drawing, setDrawing] = useState<number | undefined>(undefined);
  const drawn = fills ? drawnAt(room, drawing, zoom) : undefined;
  const scrolled = useHoldsTheCentre(frameRef, zoom);
  // The window is what a pan moves, so the gesture belongs to whatever owns the window.
  // Anything laid over the drawing keeps its own presses: a pin is dragged, not panned.
  // Armed by the drawing being bigger than its window rather than by the level: a tall
  // drawing runs past the bottom at the fitted size too, and dragging is how it is
  // reached. Measured with a pixel to spare, so a drawing that rounds over the window it
  // was fitted to does not slide under the pointer.
  const pan = usePan(frameRef, overflowing(room, drawn));
  const whole =
    fills && room !== undefined && drawing !== undefined && room.width > 0
      ? wholeLevel(room, drawing)
      : undefined;
  useEffect(() => {
    if (whole !== undefined) onWhole?.(whole);
  }, [whole, onWhole]);

  return (
    <div
      ref={frameRef}
      data-testid={testId}
      onScroll={scrolled}
      onPointerDownCapture={pan.begin}
      onClickCapture={pan.swallow}
      className={cn(
        "rounded-md border border-border",
        // A drawing stepped below the fit is narrower than its window, and a sheet on
        // white against white would put unusable margins next to the artwork with nothing
        // to tell them apart. The window is a surface the sheet sits on.
        fills ? "bg-muted" : "bg-card",
        // The scrollbars are hidden rather than absent: dragging the artwork is the
        // gesture that moves it, and a bar down each edge would take room from the
        // drawing to offer a second one.
        fills
          ? "scrollbars-hidden flex min-h-0 flex-1 overflow-auto"
          : "overflow-hidden",
      )}
    >
      {/* Centred by its own margins rather than by the frame's alignment: a flex-centred
          child wider than its scroll container has its leading edge put out of reach, and
          a zoomed drawing is exactly that child. */}
      <div
        data-testid="define-drawing-box"
        style={
          drawn === undefined
            ? undefined
            : {
                width: `${String(drawn.width)}px`,
                height: `${String(drawn.height)}px`,
              }
        }
        className={cn(
          "relative w-full",
          fills && "m-auto shrink-0 bg-card",
          pan.cursor,
        )}
      >
        <img
          src={thumbnailUrl}
          // Named by its file rather than described: what the drawing shows is
          // what the attorney is being asked to write in the field beside it, so
          // any wording here would be this screen inventing its own answer.
          alt={filename}
          onError={onError}
          onLoad={(event) => {
            const { naturalWidth, naturalHeight } = event.currentTarget;
            if (naturalHeight > 0) setDrawing(naturalWidth / naturalHeight);
          }}
          className="block h-auto w-full"
        />
        {children}
      </div>
    </div>
  );
}
